import { Link } from 'react-router-dom';

export default function CodeOfConductPage() {
    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            {/* Header */}
            <div className="text-center mb-8 animate-fade-in">
                <div className="inline-block bg-primary-950 text-primary-300 border border-primary-500/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-2">
                    Fall 2026 Program Standards
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-extrabold text-white mb-3">
                    Code of <span className="text-primary-500">Conduct</span>
                </h1>
                <p className="text-secondary-300 text-base sm:text-lg">Peck Pirates Esports • Peck Community Schools</p>

                {/* PDF Download Action Bar */}
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                    <a
                        href="/documents/Code-of-Conduct-Fall-2026.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-primary-600 hover:bg-primary-500 text-white font-bold text-xs px-4 py-2.5 rounded-lg transition-colors flex items-center gap-2 shadow-md"
                    >
                        <span>📄 Download Code of Conduct & Auth Form (PDF)</span>
                        <span>↗</span>
                    </a>
                    <a
                        href="/documents/Peck_Pirates_Esports_Fall_2026_Packet.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-secondary-800 hover:bg-secondary-700 text-secondary-200 text-xs px-4 py-2.5 rounded-lg border border-secondary-600 transition-colors flex items-center gap-1.5"
                    >
                        <span>📚 Full Season Packet (PDF)</span>
                    </a>
                </div>
            </div>

            {/* Core Values & PIRATE Code */}
            <section className="card mb-6 p-6 bg-secondary-900 border-secondary-700 animate-slide-up">
                <h2 className="text-xl font-display font-bold text-primary-400 mb-3">
                    I. Core Values & The PIRATE Code
                </h2>
                <p className="text-secondary-300 text-sm leading-relaxed mb-4">
                    As a Peck Pirate, you represent your school, teammates, and community. Participation in esports is a <strong className="text-white">privilege</strong>, and all athletes are expected to uphold the highest standards of digital citizenship, sportsmanship, and integrity.
                </p>
                <div className="bg-primary-950/60 border border-primary-500/40 rounded-xl p-4 text-center">
                    <div className="text-xs uppercase tracking-widest text-primary-400 font-bold mb-1">The Peck Esports Standard</div>
                    <div className="font-display font-bold text-white text-base sm:text-lg tracking-wide">
                        Proud • Inclusive • Respectful • Accountable • Trustworthy • Engaged • Safe
                    </div>
                </div>
            </section>

            {/* Online & In-Game Behavior */}
            <section className="card mb-6 p-6 bg-secondary-900 border-secondary-700 animate-slide-up">
                <h2 className="text-xl font-display font-bold text-primary-400 mb-4">
                    II. Online & In-Game Behavior
                </h2>
                <ul className="space-y-4 text-sm text-secondary-300">
                    <li className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-primary-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-primary-400 font-bold">
                            💬
                        </div>
                        <div>
                            <strong className="text-white block">Respectful Communication</strong>
                            <p className="text-secondary-400 text-xs mt-0.5 leading-relaxed">
                                Toxic behavior, offensive trash-talking, harassment, or unsportsmanlike conduct in game chat, match lobbies, team Discord, or anywhere online is strictly prohibited and subject to school disciplinary action.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-red-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-red-400 font-bold">
                            🚫
                        </div>
                        <div>
                            <strong className="text-white block">Zero Tolerance Policy</strong>
                            <p className="text-secondary-400 text-xs mt-0.5 leading-relaxed">
                                Discrimination, hate speech, cyberbullying, or harassment of any kind will result in immediate suspension from competition and administrative review per District Policy and the Athletic Handbook.
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-green-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-green-400 font-bold">
                            ⚖️
                        </div>
                        <div>
                            <strong className="text-white block">Fair Play & Competitive Integrity</strong>
                            <p className="text-secondary-400 text-xs mt-0.5 leading-relaxed">
                                Cheating, hacking, exploiting glitches, ringers, or intentional disconnection is strictly banned and treated with the same severity as academic dishonesty.
                            </p>
                        </div>
                    </li>
                </ul>
            </section>

            {/* Platform, Discord & Device Policy */}
            <section className="card mb-6 p-6 bg-secondary-900 border-secondary-700 animate-slide-up">
                <h2 className="text-xl font-display font-bold text-primary-400 mb-4">
                    III. Technology, Discord & Device Policy
                </h2>
                <ul className="space-y-4 text-sm text-secondary-300">
                    <li className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-blue-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-blue-400 font-bold">
                            🔒
                        </div>
                        <div>
                            <strong className="text-white block">Team Discord & Platform Safety (Ages 13+)</strong>
                            <p className="text-secondary-400 text-xs mt-0.5 leading-relaxed">
                                Access to the Team Discord is restricted to students ages 13+ with signed parental consent. Parents are welcome to join. All interactions are moderated under school Acceptable Use Policies (AUP).
                            </p>
                        </div>
                    </li>
                    <li className="flex items-start gap-3">
                        <div className="w-7 h-7 bg-purple-500/20 rounded-lg flex items-center justify-center shrink-0 mt-0.5 text-purple-400 font-bold">
                            📱
                        </div>
                        <div>
                            <strong className="text-white block">Wireless Communication Devices (WCD Policy)</strong>
                            <p className="text-secondary-400 text-xs mt-0.5 leading-relaxed">
                                Personal devices during esports practice and competition may be used only for program-related activities with coach approval. Photographing or recording other students without consent is strictly prohibited.
                            </p>
                        </div>
                    </li>
                </ul>
            </section>

            {/* Academic & Athletic Integration */}
            <section className="card mb-8 p-6 bg-secondary-900 border-secondary-700 animate-slide-up">
                <h2 className="text-xl font-display font-bold text-primary-400 mb-3">
                    IV. Academic Eligibility & Athletic Alignment
                </h2>
                <p className="text-secondary-300 text-sm leading-relaxed mb-3">
                    Peck Esports follows the <strong>Peck Community Schools Athletic Handbook</strong> for all academic eligibility, attendance rules, and behavioral guidelines:
                </p>
                <div className="grid sm:grid-cols-2 gap-3 text-xs text-secondary-300">
                    <div className="bg-secondary-800 p-3 rounded-lg border border-secondary-700">
                        <strong className="text-white block mb-1">📚 No "E" Academic Rule</strong>
                        Eligibility checks run Friday to Friday. A failing grade results in a one-week suspension from official matches.
                    </div>
                    <div className="bg-secondary-800 p-3 rounded-lg border border-secondary-700">
                        <strong className="text-white block mb-1">🏫 School Day Attendance</strong>
                        Students must attend school on competition days to participate in practice or matches unless pre-excused by administration.
                    </div>
                </div>
            </section>

            {/* Participation Form Info & Download Card */}
            <div className="card border-2 border-primary-500/60 bg-gradient-to-br from-primary-950/40 via-secondary-900 to-secondary-950 p-6 sm:p-8 text-center mb-8">
                <h3 className="text-2xl font-display font-bold text-white mb-2">
                    Submit Your Signed Authorization Form
                </h3>
                <p className="text-secondary-300 text-sm max-w-xl mx-auto mb-6">
                    All participating students and their parent/guardian must complete the official Participant Authorization Form before participating in official matches or field trips.
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                    <a
                        href="/documents/Code-of-Conduct-Fall-2026.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download and print Code of Conduct and Authorization Form PDF (opens in new tab)"
                        className="bg-yellow-400 hover:bg-yellow-300 text-primary-950 font-extrabold text-sm px-6 py-3 rounded-xl transition-colors shadow-lg focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:outline-none"
                    >
                        Download & Print Form (PDF) ↗
                    </a>
                    <a
                        href="https://forms.gle/2GvpB888G5xRci1S9"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Fill out online Google Signup Form (opens in new tab)"
                        className="bg-primary-600 hover:bg-primary-500 text-white font-bold text-sm px-6 py-3 rounded-xl transition-colors focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
                    >
                        Fill Out Online Interest Form ↗
                    </a>
                </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-4 border-t border-secondary-800">
                <Link to="/schedule" className="btn-secondary text-sm focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none">
                    ← Schedule
                </Link>
                <Link to="/handbook" className="btn-primary text-sm focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none">
                    Season Handbook →
                </Link>
            </div>

        </div>
    );
}

